<template>
  <div class="sensor-accordion-panel" :aria-label="ariaLabel" :style="panelStyle">
    <div class="sensor-accordion-panel__list">
      <button
        v-for="item in items"
        :key="item.key"
        class="sensor-accordion-panel__card"
        :class="{ 'sensor-accordion-panel__card--active': currentKey === item.key }"
        type="button"
        :aria-expanded="currentKey === item.key"
        :aria-controls="`sensor-accordion-panel-description-${item.key}`"
        @pointerenter="scheduleActivate(item.key)"
        @pointerleave="cancelHoverActivation"
        @click="activateKey(item.key)"
      >
        <div class="sensor-accordion-panel__card-head">
          <div class="sensor-accordion-panel__copy">
            <h3 class="sensor-accordion-panel__title">{{ item.title }}</h3>
            <div
              class="sensor-accordion-panel__description-wrap"
              :aria-hidden="currentKey !== item.key"
            >
              <p
                :id="`sensor-accordion-panel-description-${item.key}`"
                class="sensor-accordion-panel__description"
              >
                {{ item.description }}
              </p>
            </div>
          </div>
        </div>
      </button>
    </div>

    <figure
      class="sensor-accordion-panel__media"
      :class="{ 'sensor-accordion-panel__media--contain': activeItem?.imageFit === 'contain' }"
      :aria-label="mediaLabel"
    >
      <transition name="sensor-accordion-panel__image">
        <img
          :key="currentKey"
          class="sensor-accordion-panel__image"
          :class="{ 'sensor-accordion-panel__image--contain': activeItem?.imageFit === 'contain' }"
          :src="activeItem?.image"
          :alt="activeItem?.imageAlt || activeItem?.title || ''"
          :style="{ objectPosition: activeItem?.imagePosition || '' }"
          loading="lazy"
        />
      </transition>
    </figure>
  </div>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, ref } from 'vue'

export interface SensorAccordionPanelItem {
  key: string
  title: string
  description: string
  image: string
  imageAlt?: string
  imageFit?: 'cover' | 'contain'
  imagePosition?: string
}

const props = withDefaults(defineProps<{
  items: SensorAccordionPanelItem[]
  ariaLabel?: string
  mediaLabel?: string
  defaultActiveKey?: string
}>(), {
  ariaLabel: '',
  mediaLabel: '',
  defaultActiveKey: ''
})

const getFirstKey = () => props.items[0]?.key || ''
const activeKey = ref(props.defaultActiveKey || getFirstKey())

const activeItem = computed(() => (
  props.items.find((item) => item.key === activeKey.value) || props.items[0]
))

const currentKey = computed(() => (
  props.items.some((item) => item.key === activeKey.value) ? activeKey.value : getFirstKey()
))

const panelStyle = computed(() => ({
  '--sensor-item-count': String(Math.max(props.items.length, 1)),
  '--sensor-row-tracks': props.items.length
    ? props.items
      .map((item) => item.key === currentKey.value ? 'var(--sensor-card-expanded-height)' : 'var(--sensor-card-collapsed-height)')
      .join(' ')
    : '1fr'
}))

let hoverActivationTimer: ReturnType<typeof window.setTimeout> | null = null

const cancelHoverActivation = () => {
  if (hoverActivationTimer !== null) {
    window.clearTimeout(hoverActivationTimer)
    hoverActivationTimer = null
  }
}

const activateKey = (key: string) => {
  cancelHoverActivation()
  if (key !== activeKey.value) {
    activeKey.value = key
  }
}

const scheduleActivate = (key: string) => {
  cancelHoverActivation()
  hoverActivationTimer = window.setTimeout(() => {
    activateKey(key)
  }, 64)
}

onBeforeUnmount(() => {
  cancelHoverActivation()
})
</script>

<style scoped>
.sensor-accordion-panel {
  width: min(100%, 1200px);
  margin: 0 auto;
  padding: 0 var(--sensor-shell-padding, var(--pixso-gap-md));
  display: grid;
  grid-template-columns: minmax(0, 0.92fr) minmax(0, 1.08fr);
  gap: var(--sensor-grid-gap, var(--pixso-gap-md));
  align-items: stretch;
  --sensor-gap: var(--pixso-gap-sm);
  --sensor-panel-height: 600px;
  --sensor-card-collapsed-height: 106px;
  --sensor-card-expanded-height: calc((var(--sensor-panel-height) - (var(--sensor-item-count) - 1) * var(--sensor-gap) - (var(--sensor-item-count) - 1) * var(--sensor-card-collapsed-height)));
  --sensor-card-radius: calc(var(--pixso-radius-lg) - var(--pixso-gap-sm));
  --sensor-media-radius: var(--pixso-radius-lg);
  --sensor-trigger-padding-block: var(--pixso-fs-16);
  --sensor-trigger-padding-inline: calc(var(--pixso-gap-lg) - var(--pixso-gap-xs) * 2);
  --sensor-copy-gap: calc(var(--pixso-gap-sm) - var(--pixso-gap-xs));
  --sensor-ease: cubic-bezier(0.16, 1, 0.3, 1);
  --sensor-card-duration: 400ms;
  --sensor-content-duration: 300ms;
}

.sensor-accordion-panel__list {
  display: grid;
  grid-template-rows: var(--sensor-row-tracks);
  gap: var(--sensor-gap);
  height: var(--sensor-panel-height);
  align-content: start;
  transition: grid-template-rows var(--sensor-card-duration) var(--sensor-ease);
}

.sensor-accordion-panel__card {
  width: 100%;
  border: 0;
  border-radius: var(--sensor-card-radius);
  background: var(--pixso-color-surface);
  color: var(--pixso-color-text);
  text-align: left;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  justify-content: center;
  min-height: 0;
  height: 100%;
  padding: var(--sensor-trigger-padding-block) var(--sensor-trigger-padding-inline);
  cursor: pointer;
  font: inherit;
  transition: background-color 180ms ease, transform var(--sensor-card-duration) var(--sensor-ease);
  will-change: transform;
  transform: translateZ(0);
}

.sensor-accordion-panel__card:hover {
  transform: translateZ(0);
}

.sensor-accordion-panel__card--active {
  background: var(--pixso-color-surface);
}

.sensor-accordion-panel__card-head {
  display: flex;
  width: 100%;
  align-items: flex-start;
}

.sensor-accordion-panel__copy {
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: var(--sensor-copy-gap);
}

.sensor-accordion-panel__title {
  margin: 0;
  font-family: var(--pixso-font-cn);
  font-weight: 700;
  font-size: var(--pixso-fs-32);
  line-height: var(--pixso-lh-32);
  color: var(--pixso-color-muted);
  transition: color 180ms ease;
}

.sensor-accordion-panel__card--active .sensor-accordion-panel__title {
  color: var(--pixso-color-primary);
}

.sensor-accordion-panel__description {
  margin: 0;
  color: var(--pixso-color-muted);
  font-family: var(--pixso-font-cn);
  font-weight: 700;
  font-size: var(--pixso-fs-16);
  line-height: var(--pixso-lh-16);
  overflow: hidden;
  min-height: 0;
  opacity: 0;
  transition: opacity var(--sensor-content-duration) var(--sensor-ease);
}

.sensor-accordion-panel__card--active .sensor-accordion-panel__description {
  color: var(--pixso-color-muted);
  opacity: 1;
}

.sensor-accordion-panel__description-wrap {
  display: grid;
  grid-template-rows: 0fr;
  transition: grid-template-rows var(--sensor-card-duration) var(--sensor-ease);
}

.sensor-accordion-panel__card--active .sensor-accordion-panel__description-wrap {
  grid-template-rows: 1fr;
}

.sensor-accordion-panel__media {
  position: relative;
  margin: 0;
  border-radius: var(--sensor-media-radius);
  overflow: hidden;
  background: var(--pixso-color-surface);
  height: var(--sensor-panel-height);
  transform: translateZ(0);
  isolation: isolate;
}

.sensor-accordion-panel__media--contain {
  display: block;
}

.sensor-accordion-panel__image {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  display: block;
  object-fit: cover;
}

.sensor-accordion-panel__image--contain {
  object-fit: contain;
  object-position: center;
  padding: clamp(18px, 2.4vw, 36px);
  box-sizing: border-box;
}

.sensor-accordion-panel__image-enter-active,
.sensor-accordion-panel__image-leave-active {
  transition: opacity 300ms var(--sensor-ease), transform 420ms var(--sensor-ease);
}

.sensor-accordion-panel__image-enter-from,
.sensor-accordion-panel__image-leave-to {
  opacity: 0;
  transform: scale(1.012);
}

@media (max-width: 1024px) {
  .sensor-accordion-panel {
    grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
    --sensor-panel-height: 560px;
  }
}

@media (max-width: 768px) {
  .sensor-accordion-panel {
    width: 100%;
    max-width: none;
    grid-template-columns: 1fr;
    padding: 0;
    gap: var(--pixso-fs-16);
    --sensor-card-collapsed-height: auto;
    --sensor-card-expanded-height: auto;
  }

  .sensor-accordion-panel__list {
    height: auto;
    grid-template-rows: none;
  }

  .sensor-accordion-panel__card {
    height: auto;
    padding: var(--pixso-fs-16) var(--pixso-fs-20);
  }

  .sensor-accordion-panel__media {
    height: 360px;
    border-radius: calc(var(--pixso-radius-lg) - var(--pixso-gap-sm));
  }

  .sensor-accordion-panel__image--contain {
    padding: var(--pixso-fs-16);
  }
}

@media (prefers-reduced-motion: reduce) {
  .sensor-accordion-panel__list,
  .sensor-accordion-panel__card,
  .sensor-accordion-panel__title,
  .sensor-accordion-panel__description-wrap,
  .sensor-accordion-panel__description,
  .sensor-accordion-panel__image-enter-active,
  .sensor-accordion-panel__image-leave-active {
    transition: none;
  }

  .sensor-accordion-panel__image-enter-from,
  .sensor-accordion-panel__image-leave-to {
    transform: none;
  }
}
</style>
